import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button, PasswordInput } from '@aqarmap/aqarmap-ui';
import useFormValidations from '~/hooks/useFormValidations';
import {
  useGetUserProfileQuery,
  useUpdatePasswordMutation,
} from '~/redux/api/authApiSlice';
import ErrorMessage from '~/components/ErrorMessage';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { objectSerialize } from '~/lib';
import PageLayout from '~myAccount/components/PageLayout';
import { useFullRoutes } from '~/hooks';

const PasswordUpdate = () => {
  const navigate = useNavigate();
  const { MY_ACCOUNT_URL } = useFullRoutes();
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const { data: userProfile } = useGetUserProfileQuery();
  const hasPassword = userProfile?.has_password;

  const [errorMessage, setErrorMessage] = useState('');

  const { passwordSchema, confirmPasswordSchema } = useFormValidations();

  const updatePasswordSchema = z
    .object({
      currentPassword: hasPassword ? passwordSchema : z.string().optional(),
      newPassword: passwordSchema,
      confirmPassword: confirmPasswordSchema,
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t('passwordNotMatch'),
      path: ['confirmPassword'],
    });

  type IPasswordUpdate = z.infer<typeof updatePasswordSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IPasswordUpdate>({
    resolver: zodResolver(updatePasswordSchema),
    mode: 'onChange',
  });
  const [updatePassword, { isSuccess, isLoading }] =
    useUpdatePasswordMutation();

  const onSubmit = async ({
    confirmPassword,
    newPassword,
    currentPassword,
  }: IPasswordUpdate) => {
    const formData = objectSerialize({
      'plainPassword[first]': newPassword,
      'plainPassword[second]': confirmPassword,
      currentPassword: currentPassword,
    });
    if (!hasPassword) formData.delete('currentPassword');

    const res: any = await updatePassword(formData);
    if (res.data && !res.error) {
      navigate(MY_ACCOUNT_URL.ROOT);
    }
    if (res.error) {
      setErrorMessage(t('failedChangePassword'));
    }
  };
  const renderMessages = errorMessage || isSuccess;

  return (
    <PageLayout
      title={t('privacy')}
      bodyClassName="border border-gray rounded-[8px]"
    >
      <h2 className="text-title_4 mb-1.5x p-3x pb-0">
        {hasPassword ? t('changePassword') : t('setPassword')}
      </h2>
      <form
        className="w-full flex flex-col gap-3x"
        onSubmit={handleSubmit(onSubmit)}
      >
        {renderMessages && (
          <div>{errorMessage && <ErrorMessage message={errorMessage} />}</div>
        )}

        <div className="flex flex-col gap-3x px-3x">
          {hasPassword && (
            <Controller
              control={control}
              name="currentPassword"
              render={({ field }) => (
                <PasswordInput
                  field={{
                    ...field,
                    placeholder: t('currentPassword'),
                    autoFocus: true,
                    id: 'currentPassword',
                  }}
                  error={errors[field.name]?.message}
                  label={{
                    text: t('currentPassword'),
                    className: 'font-[400] block',
                  }}
                />
              )}
            />
          )}
          <div
            className={`${hasPassword ? 'lg:flex-row lg:gap-5x' : ''} flex flex-col gap-3x`}
          >
            <Controller
              control={control}
              name="newPassword"
              render={({ field }) => (
                <PasswordInput
                  field={{
                    ...field,
                    placeholder: t('newPassword'),
                    autoFocus: hasPassword ? false : true,
                    id: 'newPassword',
                  }}
                  error={errors['newPassword']?.message}
                  label={{
                    text: t('newPassword'),
                    className: 'font-[400] block',
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name="confirmPassword"
              render={({ field }) => (
                <PasswordInput
                  field={{
                    ...field,
                    placeholder: t('confirmPassword'),
                    id: 'confirmPassword',
                  }}
                  error={errors['confirmPassword']?.message}
                  label={{
                    text: t('confirmPassword'),
                    className: 'font-[400] block',
                  }}
                />
              )}
            />
          </div>
        </div>
        <div className="px-3x">
          <a
            href={`${process.env.REACT_APP_AUTH_BASE_URL}/${locale}/reset-password`}
            className="text-link-1 text-blue"
          >
            {t('forgetPassword')}
          </a>
        </div>
        <div className="flex items-center gap-2x p-3x border-t border-gray">
          <Button
            size="medium"
            ariaLabel="submit-btn"
            type="submit"
            variant="primary"
            isLoading={isLoading}
            disabled={isLoading}
            className="w-auto"
          >
            {t('save')}
          </Button>

          <Button
            size="medium"
            ariaLabel="cancel-btn"
            type="button"
            variant="secondary"
            className="w-auto"
            onClick={() => navigate(-1)}
          >
            {t('cancel')}
          </Button>
        </div>
      </form>
    </PageLayout>
  );
};

export default PasswordUpdate;
